/**
 * Role-Based Config Filtering
 *
 * TODO: Move role-based filtering to SQL
 * Currently, the SQL effective_config returns all fields and this TypeScript layer
 * filters by role. This should be moved to SQL for better efficiency:
 * - Reduces payload size (only return fields the role can see)
 * - Single source of truth for role permissions
 * - Removes need for client-side filtering
 *
 * Implementation approach:
 * - Add role parameter to effective_config SQL function
 * - Filter fields based on readableByRoles in SQL
 * - Remove filterConfigByRole from TypeScript layer
 */

import type { HopDriveConfig } from '../_generated/hopdrive-config-schema.js';
import { allDefinitions } from '../definitions/index.js';
import { type Role, type ConfigLevel, type SqlMetadata, type ConfigMetadata, type SettingMetadata } from '../types.js';
import { getJsonSchemaForSetting } from './json-schema-lookup.js';

/**
 * Filters a config object to only include settings readable by the specified role.
 * If no role is provided, returns the config unchanged.
 * Skips the __metadata property if present.
 *
 * TODO: This filtering should be moved to SQL for efficiency. See file header.
 *
 * @param config - The config object to filter
 * @param role - The role to filter by (optional)
 * @returns Filtered config with only settings readable by the role
 */
export function filterConfigByRole(config: HopDriveConfig, role?: Role): HopDriveConfig {
  if (!role) {
    // Still need to strip __metadata if present
    const { __metadata, ...rest } = config as HopDriveConfig & { __metadata?: unknown };
    return rest;
  }

  const filtered: HopDriveConfig = {};

  for (const [key, value] of Object.entries(config)) {
    // Skip __metadata property
    if (key === '__metadata') continue;

    const definition = allDefinitions[key as keyof typeof allDefinitions];

    // If definition exists and role is in readableByRoles, include it
    if (definition && definition.readableByRoles.includes(role)) {
      (filtered as Record<string, unknown>)[key] = value;
    }
  }

  return filtered;
}

/**
 * Enriches SQL __metadata with TypeScript setting definitions.
 * Only includes settings that exist in the merged config.
 *
 * @param sqlMetadata - Raw metadata from SQL effective_config
 * @param mergedConfig - The merged config (after filtering)
 * @returns Enriched ConfigMetadata with per-setting information
 */
export function enrichMetadata(sqlMetadata: SqlMetadata, mergedConfig: HopDriveConfig): ConfigMetadata {
  const settings: Record<string, SettingMetadata> = {};

  // Only add setting info for keys that are in the merged config
  for (const key of Object.keys(mergedConfig)) {
    // Skip __metadata if it somehow got into the config
    if (key === '__metadata') continue;

    const definition = allDefinitions[key as keyof typeof allDefinitions];
    if (definition) {
      // Get JSON schema for this definition
      const jsonSchema = getJsonSchemaForSetting(key);

      // Extract only type, properties, and additionalProperties from JSON schema
      const simplifiedSchema = jsonSchema
        ? {
            ...(jsonSchema.type && { type: jsonSchema.type }),
            ...(jsonSchema.properties && { properties: jsonSchema.properties }),
            ...(jsonSchema.additionalProperties !== undefined && {
              additionalProperties: jsonSchema.additionalProperties,
            }),
          }
        : null;

      settings[key] = {
        schema: simplifiedSchema,
        label: definition.label,
        description: definition.description,
        ...(definition.warning && { warning: definition.warning }),
        kind: definition.kind,
        ...(definition.category && { category: definition.category }),
        writableAtLevels: definition.writableAtLevels,
        readableByRoles: definition.readableByRoles,
        ...(definition.defaultValue !== undefined && { defaultValue: definition.defaultValue }),
        ...(definition.deprecated && { deprecated: definition.deprecated }),
        sourceLevel: sqlMetadata.sourceLevels[key] as ConfigLevel,
      };
    }
  }

  return {
    ...sqlMetadata,
    settings,
  };
}
